import React from 'react';
import { Card, List, Tag, Button, Empty } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { resolveFaIconClasses } from '../../utils/faIconUtils';
import {
  followUpStatusLabel,
  formatFollowUpDate,
  resolveFollowUpEntity,
} from '../../utils/followUpDisplayUtils';

const ActivityIcon = ({ activity }) => {
  if (!activity?.icon) return null;
  const resolved = resolveFaIconClasses(activity.icon);
  if (!resolved) return null;
  return (
    <i
      className={resolved.className}
      style={{ color: activity.color || '#7c3aed', fontSize: 14 }}
      aria-hidden
    />
  );
};

const FollowUpPreviewItem = ({ item }) => {
  const entity = resolveFollowUpEntity(item);
  return (
    <List.Item className="custom-font">
      <div className="flex w-full flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <ActivityIcon activity={item.activity} />
            <span className="font-medium text-gray-800 dark:text-gray-100 truncate">
              {item.activity?.name || 'فعالیت'}
            </span>
            {entity.type ? (
              <Tag className="m-0 text-[10px]">{entity.type === 'deal' ? 'معامله' : 'سرنخ'}</Tag>
            ) : null}
          </div>
          <div className="text-xs text-gray-500 truncate">{entity.name}</div>
          {item.remark ? (
            <div className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2">{item.remark}</div>
          ) : null}
          <div className="text-[11px] text-gray-400">{formatFollowUpDate(item.next_follow_up_date)}</div>
        </div>
        <Tag color={item.status === 'completed' ? 'green' : 'gold'} className="self-start sm:self-center">
          {followUpStatusLabel(item.status)}
        </Tag>
      </div>
    </List.Item>
  );
};

const ProjectActivityItem = ({ item }) => (
  <List.Item className="custom-font">
    <div className="w-full">
      <div className="text-sm text-gray-800 dark:text-gray-100">{item.activity}</div>
      {item.project?.project_name ? (
        <div className="text-xs text-gray-500">پروژه: {item.project.project_name}</div>
      ) : null}
    </div>
  </List.Item>
);

const UserActivityItem = ({ item }) => (
  <List.Item className="custom-font">
    <div className="w-full text-sm text-gray-800 dark:text-gray-100">
      {item.activity || item.description || item.heading || '—'}
    </div>
  </List.Item>
);

export default function DashboardActivitiesCard({
  followUps = [],
  projectActivities = [],
  userActivities = [],
  viewAllHref = '/dashboard/follow-ups',
  maxItems = 6,
}) {
  const hasFollowUps = followUps.length > 0;
  const hasProject = projectActivities.length > 0;
  const hasUser = userActivities.length > 0;

  if (!hasFollowUps && !hasProject && !hasUser) {
    return (
      <Card title="فعالیت‌ها" className="border border-gray-100 dark:border-gray-700">
        <Empty description="فعالیتی ثبت نشده است" />
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <Card
        title="پیگیری‌ها و فعالیت‌های CRM"
        extra={
          <Link to={viewAllHref}>
            <Button type="link" size="small" icon={<ArrowLeftOutlined />} className="custom-font">
              مشاهده همه
            </Button>
          </Link>
        }
        className="border border-gray-100 dark:border-gray-700"
      >
        {hasFollowUps ? (
          <List
            size="small"
            dataSource={followUps.slice(0, maxItems)}
            renderItem={(item) => <FollowUpPreviewItem item={item} />}
          />
        ) : (
          <Empty description="پیگیری معلقی نیست" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>

      <div className="space-y-4">
        {hasProject ? (
          <Card title="فعالیت پروژه‌ها" className="border border-gray-100 dark:border-gray-700">
            <List
              size="small"
              dataSource={projectActivities.slice(0, maxItems)}
              renderItem={(item) => <ProjectActivityItem item={item} />}
            />
          </Card>
        ) : null}
        {hasUser ? (
          <Card title="فعالیت کاربران" className="border border-gray-100 dark:border-gray-700">
            <List
              size="small"
              dataSource={userActivities.slice(0, maxItems)}
              renderItem={(item) => <UserActivityItem item={item} />}
            />
          </Card>
        ) : null}
      </div>
    </div>
  );
}
